<template>
  <CRow>
    <CCol sm="12">
      <CCard>
        <CCardHeader class="d-flex flex-wrap align-items-center">
          <div>
            <CIcon name="cil-pencil" />
            <h5 class="d-inline ml-2">Manual Invoices</h5>
          </div>
          <div class="ml-auto">
            <CButton
              v-if="canManage"
              color="primary"
              :to="{ name: 'Create Manual Invoice' }"
            >
              <CIcon name="cil-plus" /> Create draft
            </CButton>
          </div>
        </CCardHeader>

        <CCardBody>
          <CAlert color="info">
            Use this screen for exceptional sales to a private individual or an external company.
            Drafts remain editable and receive their official number only when finalized.
          </CAlert>

          <CRow class="mb-3">
            <CCol md="4" xl="3">
              <CInput
                label="Search"
                placeholder="Number, name, VAT or email"
                :value.sync="filters.search"
                @keyup.enter="applyFilters"
              />
            </CCol>
            <CCol md="4" xl="3">
              <CSelect
                label="Billing entity"
                :value.sync="filters.billing_entity_id"
                :options="billingEntityOptions"
                :disabled="loadingEntities"
              />
            </CCol>
            <CCol md="4" xl="2">
              <CSelect
                label="Recipient"
                :value.sync="filters.recipient_type"
                :options="recipientTypeOptions"
              />
            </CCol>
            <CCol md="4" xl="2">
              <CSelect
                label="Status"
                :value.sync="filters.invoiceFilter"
                :options="statusOptions"
              />
            </CCol>
            <CCol md="4" xl="2">
              <CInput type="date" label="From" :value.sync="filters.date_from" />
            </CCol>
            <CCol md="4" xl="2">
              <CInput type="date" label="To" :value.sync="filters.date_to" />
            </CCol>
          </CRow>

          <div class="d-flex flex-wrap mb-3 invoice-actions">
            <CButton color="info" @click="applyFilters" :disabled="loading">
              <CIcon name="cil-filter" /> Apply filters
            </CButton>
            <CButton color="secondary" variant="outline" @click="resetFilters" :disabled="loading">
              Reset
            </CButton>
            <CButton color="primary" variant="outline" @click="downloadZip" :disabled="busy || !total">
              <CIcon name="cil-cloud-download" /> Download PDF/UBL ZIP
            </CButton>
          </div>

          <CDataTable
            striped
            hover
            responsive
            :items="invoices"
            :fields="fields"
            :loading="loading"
            :sorter="{ external: true, resetable: true }"
            :itemsPerPageSelect="{ external: true, values: [10, 25, 50, 100, 200] }"
            :items-per-page.sync="itemsPerPage"
            :noItemsView="{
              noResults: 'No manual invoices match the filters.',
              noItems: 'No manual invoices have been created yet.'
            }"
            @update:sorter-value="changeSort"
            @pagination-change="changePageSize"
          >
            <template #ivNr="{ item }">
              <td>
                <strong>{{ item.is_finalized ? item.ivNr : `Draft #${item.id}` }}</strong>
              </td>
            </template>

            <template #recipient="{ item }">
              <td>
                <div><strong>{{ item.ivName }}</strong></div>
                <small class="text-muted">
                  {{ recipientTypeLabel(item.recipient_type) }}
                  <span v-if="item.recipient_email"> · {{ item.recipient_email }}</span>
                </small>
              </td>
            </template>

            <template #billing_entity="{ item }">
              <td>
                {{ item.billing_entity ? item.billing_entity.code : item.billing_entity_code }}
              </td>
            </template>

            <template #ivDate="{ item }">
              <td>{{ formatDate(item.ivDate) }}</td>
            </template>

            <template #payment_due_date="{ item }">
              <td>{{ formatDate(item.payment_due_date) }}</td>
            </template>

            <template #ivTotIncl="{ item }">
              <td class="text-right"><strong>{{ money(item.ivTotIncl) }}</strong></td>
            </template>

            <template #status="{ item }">
              <td>
                <CBadge v-if="!item.is_finalized" color="dark">Draft</CBadge>
                <template v-else>
                  <CBadge :color="paymentStatusColor(item.payment_status)">
                    {{ paymentStatusLabel(item.payment_status) }}
                  </CBadge>
                  <CBadge class="ml-1" :color="item.ivMailed ? 'info' : 'secondary'">
                    {{ item.ivMailed ? 'Emailed' : 'Not emailed' }}
                  </CBadge>
                  <CBadge class="ml-1" :color="item.ivTransfer ? 'primary' : 'secondary'">
                    {{ item.ivTransfer ? 'Transferred' : 'Not transferred' }}
                  </CBadge>
                </template>
              </td>
            </template>

            <template #lines="{ item }">
              <td>
                <div v-for="line in item.lines" :key="line.id" class="line-description">
                  {{ line.quantity }} × {{ line.description }}
                  <small class="text-muted">({{ formatRate(line.vat_rate) }}% VAT)</small>
                </div>
              </td>
            </template>

            <template #actions="{ item }">
              <td class="text-nowrap py-2">
                <CButton
                  v-if="canManage && item.is_editable"
                  size="sm"
                  color="info"
                  shape="pill"
                  v-c-tooltip="'Edit draft'"
                  :to="{ name: 'Edit Manual Invoice Draft', params: { id: item.id } }"
                  :disabled="busy"
                >
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton
                  v-if="canManage && item.is_editable"
                  size="sm"
                  color="success"
                  shape="pill"
                  class="ml-1"
                  v-c-tooltip="'Finalize and assign invoice number'"
                  @click="finalizeInvoice(item)"
                  :disabled="busy"
                >
                  <CIcon name="cil-check" />
                </CButton>
                <CButton
                  v-if="canManage && item.is_editable"
                  size="sm"
                  color="danger"
                  shape="pill"
                  class="ml-1"
                  v-c-tooltip="'Delete draft'"
                  @click="deleteDraft(item)"
                  :disabled="busy"
                >
                  <CIcon name="cil-trash" />
                </CButton>
                <CButton
                  v-if="item.is_finalized"
                  size="sm"
                  color="primary"
                  shape="pill"
                  v-c-tooltip="'Download PDF'"
                  @click="downloadInvoice(item, 'pdf')"
                  :disabled="busy"
                >
                  <CIcon name="cil-file" />
                </CButton>
                <CButton
                  v-if="item.is_finalized"
                  size="sm"
                  color="dark"
                  shape="pill"
                  class="ml-1"
                  v-c-tooltip="'Download UBL'"
                  @click="downloadInvoice(item, 'ubl')"
                  :disabled="busy"
                >
                  UBL
                </CButton>
                <CButton
                  v-if="canManage && item.is_finalized"
                  size="sm"
                  color="info"
                  shape="pill"
                  class="ml-1"
                  v-c-tooltip="'Email invoice'"
                  @click="sendInvoice(item)"
                  :disabled="busy"
                >
                  <CIcon name="cil-envelope-closed" />
                </CButton>
                <CButton
                  v-if="canManage && item.is_finalized"
                  size="sm"
                  color="success"
                  shape="pill"
                  class="ml-1"
                  v-c-tooltip="'Payments and history'"
                  @click="openPayment(item)"
                  :disabled="busy"
                >
                  <CIcon name="cil-euro" />
                </CButton>
              </td>
            </template>
          </CDataTable>

          <div class="d-flex flex-wrap align-items-center mt-3" v-if="total || pages > 1">
            <h6 class="mb-0">Total: <CBadge color="primary">{{ total }}</CBadge></h6>
            <CPagination
              v-if="pages > 1"
              class="ml-auto mb-0"
              :pages="pages"
              :active-page.sync="activePage"
              :dots="false"
            />
          </div>
        </CCardBody>
      </CCard>
    </CCol>

    <CModal
      title="Payments and history"
      color="success"
      centered
      size="lg"
      :show.sync="paymentModal"
    >
      <p v-if="paymentInvoice">
        Invoice <strong>{{ paymentInvoice.ivNr }}</strong> · outstanding
        <strong>{{ money(outstandingAmount(paymentInvoice)) }}</strong>
      </p>

      <div v-if="paymentInvoice && paymentInvoice.payments && paymentInvoice.payments.length" class="table-responsive mb-3">
        <table class="table table-sm table-striped mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Note</th>
              <th>Registered by</th>
              <th class="text-right">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paymentInvoice.payments" :key="item.id">
              <td>{{ formatDate(item.apdate) }}</td>
              <td>
                {{ item.apnote }}
                <CBadge v-if="item.is_reversal" color="danger" class="ml-1">Reversal</CBadge>
                <CBadge v-else-if="item.is_reversed" color="secondary" class="ml-1">Reversed</CBadge>
              </td>
              <td>{{ registeredByName(item) }}</td>
              <td class="text-right" :class="{ 'text-danger': Number(item.apamount) < 0 }">
                {{ money(item.apamount) }}
              </td>
              <td class="text-right">
                <CButton
                  v-if="!item.is_reversal && !item.is_reversed"
                  size="sm"
                  color="danger"
                  variant="outline"
                  @click="reversePayment(item)"
                  :disabled="busy"
                >
                  Reverse
                </CButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <CAlert v-else color="secondary">No payments registered yet.</CAlert>

      <template v-if="paymentInvoice && outstandingAmount(paymentInvoice) > 0">
        <h6>Register received payment</h6>
        <CInput
          type="number"
          min="0.01"
          :max="outstandingAmount(paymentInvoice)"
          step="0.01"
          label="Amount"
          :value.sync="payment.amount"
        >
          <template #prepend-content>€</template>
        </CInput>
        <CInput type="date" label="Payment date" :max="today" :value.sync="payment.date" />
        <CTextarea label="Note (optional)" rows="3" :value.sync="payment.note" />
      </template>
      <template #footer>
        <CButton color="secondary" @click="paymentModal = false">Close</CButton>
        <CButton
          v-if="paymentInvoice && outstandingAmount(paymentInvoice) > 0"
          color="success"
          @click="savePayment"
          :disabled="busy"
        >
          Save payment
        </CButton>
      </template>
    </CModal>
  </CRow>
</template>

<script>
import { Role } from '@/router/role'

const today = () => {
  const date = new Date()
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

const emptyFilters = () => ({
  search: '',
  billing_entity_id: '',
  recipient_type: '',
  invoiceFilter: 'all',
  date_from: '',
  date_to: '',
})

export default {
  name: 'ManualInvoiceList',
  data() {
    return {
      invoices: [],
      billingEntities: [],
      filters: emptyFilters(),
      activePage: Number(this.$route.query.page) || 1,
      pages: 1,
      total: 0,
      itemsPerPage: Number(process.env.VUE_APP_ITEMS_PER_PAGE) || 25,
      orderBy: 'id',
      sortAsc: false,
      loading: false,
      loadingEntities: false,
      busy: false,
      today: today(),
      paymentModal: false,
      paymentInvoice: null,
      payment: { amount: '', date: today(), note: '' },
      fields: [
        { key: 'actions', label: '', sorter: false, _style: 'min-width: 210px' },
        { key: 'ivNr', label: 'Invoice no.', _style: 'min-width: 130px' },
        { key: 'recipient', label: 'Recipient', sorter: false, _style: 'min-width: 220px' },
        { key: 'billing_entity', label: 'Entity', sorter: false, _style: 'min-width: 85px' },
        { key: 'ivDate', label: 'Date', _style: 'min-width: 105px' },
        { key: 'payment_due_date', label: 'Due date', _style: 'min-width: 105px' },
        { key: 'ivTotIncl', label: 'Total', _style: 'min-width: 100px; text-align: right' },
        { key: 'status', label: 'Status', sorter: false, _style: 'min-width: 220px' },
        { key: 'lines', label: 'Lines', sorter: false, _style: 'min-width: 260px' },
      ],
      recipientTypeOptions: [
        { value: '', label: 'All recipients' },
        { value: 'individual', label: 'Private individual' },
        { value: 'company', label: 'Company / restaurant' },
      ],
      statusOptions: [
        { value: 'all', label: 'All statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'finalized', label: 'Finalized' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'paid', label: 'Paid' },
        { value: 'not-mailed', label: 'Not emailed' },
        { value: 'mailed', label: 'Emailed' },
        { value: 'not-transfered', label: 'Not transferred' },
        { value: 'transfered', label: 'Transferred' },
      ],
    }
  },
  computed: {
    canManage() {
      return [Role.SuperAdmin, Role.Admin].some(role => this.authUserRoles.includes(role))
    },
    billingEntityOptions() {
      return [{ value: '', label: 'All billing entities' }].concat(
        this.billingEntities.map(entity => ({
          value: entity.id,
          label: `${entity.code} — ${entity.legal_name}`,
        }))
      )
    },
  },
  watch: {
    activePage(value, previous) {
      if (value === previous) return
      this.updatePageQuery()
      this.loadInvoices()
    },
  },
  async mounted() {
    await this.loadBillingEntities()
    await this.loadInvoices()
  },
  methods: {
    endpoint(template, id) {
      return template.replace('{id}', id)
    },
    async loadBillingEntities() {
      this.loadingEntities = true
      try {
        const response = await this.$axios.get(this.$backend.BILLING_ENTITIES.OPTIONS)
        this.billingEntities = response.data || []
      } catch (error) {
        this.showError(error, 'Billing entities could not be loaded.')
      } finally {
        this.loadingEntities = false
      }
    },
    async loadInvoices() {
      this.loading = true
      try {
        const response = await this.$axios.get(this.$backend.MANUAL_INVOICES.GET_ALL, {
          params: {
            page: this.activePage,
            itemsPerPage: this.itemsPerPage,
            orderBy: this.orderBy,
            asc: this.sortAsc ? 1 : 0,
            search: this.filters.search || undefined,
            billing_entity_id: this.filters.billing_entity_id || undefined,
            recipient_type: this.filters.recipient_type || undefined,
            invoiceFilter: this.filters.invoiceFilter,
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
          },
        })
        const result = response.data
        this.invoices = result.data || []
        this.total = result.total || 0
        this.pages = result.last_page || 1
        if (this.activePage > this.pages) this.activePage = 1
      } catch (error) {
        this.invoices = []
        this.total = 0
        this.showError(error, 'Manual invoices could not be loaded.')
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      if (this.filters.date_from && this.filters.date_to && this.filters.date_to < this.filters.date_from) {
        this.$toast.error('The end date cannot be before the start date.')
        return
      }
      if (this.activePage !== 1) this.activePage = 1
      else this.loadInvoices()
    },
    resetFilters() {
      this.filters = emptyFilters()
      this.applyFilters()
    },
    changeSort(sorter) {
      this.orderBy = sorter.column || 'id'
      this.sortAsc = sorter.column ? sorter.asc : false
      this.loadInvoices()
    },
    changePageSize(value) {
      this.itemsPerPage = value
      if (this.activePage !== 1) this.activePage = 1
      else this.loadInvoices()
    },
    updatePageQuery() {
      const query = Object.assign({}, this.$route.query)
      if (this.activePage > 1) query.page = this.activePage
      else delete query.page
      this.$router.replace({ name: this.$route.name, query }).catch(() => {})
    },
    async downloadInvoice(invoice, format) {
      this.busy = true
      try {
        const response = await this.$axios.get(
          this.endpoint(this.$backend.MANUAL_INVOICES.VIEW, invoice.id),
          { params: { format }, responseType: 'blob' }
        )
        const extension = format === 'ubl' ? 'xml' : 'pdf'
        const suffix = format === 'ubl' ? '_UBL' : ''
        this.saveBlob(response.data, `Factuur_${invoice.ivNr}${suffix}.${extension}`)
      } catch (error) {
        this.showError(error, 'The invoice file could not be downloaded.')
      } finally {
        this.busy = false
      }
    },
    async finalizeInvoice(invoice) {
      const confirmation = await this.mSwal.fire({
        title: `Finalize draft #${invoice.id}?`,
        html: `This assigns a permanent invoice number for <strong>${this.money(invoice.ivTotIncl)}</strong>. The invoice can no longer be edited or deleted.`,
        icon: 'warning',
        confirmButtonText: 'Finalize invoice',
      })
      if (!confirmation.isConfirmed) return
      this.busy = true
      try {
        const response = await this.$axios.post(
          this.endpoint(this.$backend.MANUAL_INVOICES.FINALIZE, invoice.id)
        )
        this.$toast.success(response.data.message || 'Invoice finalized.')
        await this.loadInvoices()
      } catch (error) {
        this.showError(error, 'The draft could not be finalized.')
      } finally {
        this.busy = false
      }
    },
    async deleteDraft(invoice) {
      const confirmation = await this.mSwal.fire({
        title: `Delete draft #${invoice.id}?`,
        text: 'This draft has no official invoice number. Deletion cannot be undone.',
        icon: 'warning',
        confirmButtonText: 'Delete draft',
      })
      if (!confirmation.isConfirmed) return
      this.busy = true
      try {
        const response = await this.$axios.delete(
          this.endpoint(this.$backend.MANUAL_INVOICES.DELETE, invoice.id)
        )
        this.$toast.success(response.data.message || 'Draft deleted.')
        await this.loadInvoices()
      } catch (error) {
        this.showError(error, 'The draft could not be deleted.')
      } finally {
        this.busy = false
      }
    },
    async downloadZip() {
      const confirmation = await this.mSwal.fire({
        title: 'Download manual invoices?',
        text: 'The ZIP contains PDF and UBL files. Date and billing entity filters are applied.',
        icon: 'question',
        confirmButtonText: 'Download',
      })
      if (!confirmation.isConfirmed) return
      this.busy = true
      try {
        const response = await this.$axios.get(this.$backend.MANUAL_INVOICES.DOWNLOAD_ALL, {
          params: {
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
            billing_entity_id: this.filters.billing_entity_id || undefined,
          },
          responseType: 'blob',
        })
        this.saveBlob(response.data, 'Manual_invoices.zip')
      } catch (error) {
        this.showError(error, 'No invoice archive could be created for these filters.')
      } finally {
        this.busy = false
      }
    },
    async sendInvoice(invoice) {
      const confirmation = await this.mSwal.fire({
        title: `Email invoice ${invoice.ivNr}?`,
        text: `The invoice will be emailed to ${invoice.recipient_email}.`,
        icon: 'question',
        confirmButtonText: 'Send',
      })
      if (!confirmation.isConfirmed) return
      this.busy = true
      try {
        const response = await this.$axios.post(
          this.endpoint(this.$backend.MANUAL_INVOICES.SEND, invoice.id)
        )
        this.$toast.success(response.data.message || 'Invoice emailed successfully.')
        if (response.data.delivery_warning) this.$toast.warning(response.data.delivery_warning)
        await this.loadInvoices()
      } catch (error) {
        this.showError(error, 'The invoice could not be emailed.')
      } finally {
        this.busy = false
      }
    },
    openPayment(invoice) {
      this.paymentInvoice = invoice
      this.payment = {
        amount: this.outstandingAmount(invoice).toFixed(2),
        date: today(),
        note: `Manual invoice ${invoice.ivNr}`,
      }
      this.paymentModal = true
    },
    async savePayment() {
      const amount = Number(this.payment.amount)
      if (!this.paymentInvoice || !this.payment.date || !amount || amount <= 0) {
        this.$toast.error('Enter a valid payment amount and date.')
        return
      }
      if (amount > this.outstandingAmount(this.paymentInvoice)) {
        this.$toast.error('The payment cannot exceed the outstanding amount.')
        return
      }
      this.busy = true
      try {
        const response = await this.$axios.post(
          this.endpoint(this.$backend.MANUAL_INVOICES.STORE_PAYMENT, this.paymentInvoice.id),
          { amount, date: this.payment.date, note: this.payment.note || null }
        )
        this.$toast.success(response.data.message || 'Payment saved.')
        this.paymentInvoice = response.data.invoice
        this.payment = {
          amount: this.outstandingAmount(this.paymentInvoice).toFixed(2),
          date: today(),
          note: `Manual invoice ${this.paymentInvoice.ivNr}`,
        }
        await this.loadInvoices()
      } catch (error) {
        this.showError(error, 'The payment could not be saved.')
      } finally {
        this.busy = false
      }
    },
    async reversePayment(payment) {
      const result = await this.mSwal.fire({
        title: `Reverse payment #${payment.id}?`,
        text: 'A traceable negative payment will be created. The original entry remains in history.',
        input: 'textarea',
        inputLabel: 'Reason for reversal',
        inputPlaceholder: 'Explain why this payment is being corrected',
        inputValidator: value => (!value || !value.trim()) ? 'A reason is required.' : undefined,
        icon: 'warning',
        confirmButtonText: 'Create reversal',
      })
      if (!result.isConfirmed) return
      this.busy = true
      try {
        const url = this.$backend.MANUAL_INVOICES.REVERSE_PAYMENT
          .replace('{id}', this.paymentInvoice.id)
          .replace('{payment_id}', payment.id)
        const response = await this.$axios.post(url, {
          date: today(),
          note: result.value.trim(),
        })
        this.$toast.success(response.data.message || 'Payment reversed.')
        this.paymentInvoice = response.data.invoice
        this.payment.amount = this.outstandingAmount(this.paymentInvoice).toFixed(2)
        await this.loadInvoices()
      } catch (error) {
        this.showError(error, 'The payment could not be reversed.')
      } finally {
        this.busy = false
      }
    },
    outstandingAmount(invoice) {
      return Math.max(0, Number(invoice.ivTotIncl || 0) - Number(invoice.total_payment || 0))
    },
    saveBlob(data, fileName) {
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    recipientTypeLabel(type) {
      return type === 'company' ? 'Company / restaurant' : 'Private individual'
    },
    paymentStatusLabel(status) {
      if (status === 'partially_paid') return 'Partially paid'
      return status === 'paid' ? 'Paid' : 'Unpaid'
    },
    paymentStatusColor(status) {
      if (status === 'paid') return 'success'
      return status === 'partially_paid' ? 'info' : 'warning'
    },
    registeredByName(payment) {
      const user = payment.registered_by_user
      return user && typeof user === 'object'
        ? [user.first_name, user.last_name].filter(Boolean).join(' ')
        : 'System / legacy'
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10).replace(/\//g, '-') : '—'
    },
    formatRate(value) {
      return Number(value || 0).toLocaleString('nl-BE', { maximumFractionDigits: 2 })
    },
    money(value) {
      return Number(value || 0).toLocaleString('nl-BE', {
        style: 'currency',
        currency: 'EUR',
      })
    },
    showError(error, fallback) {
      const response = error && error.response && error.response.data
      const validation = response && response.errors
      const message = validation
        ? Object.values(validation).reduce((all, values) => all.concat(values), []).join(' ')
        : (response && response.message) || fallback
      this.$toast.error(message)
    },
  },
}
</script>

<style scoped>
.invoice-actions {
  gap: 0.5rem;
}
.line-description + .line-description {
  margin-top: 0.25rem;
}
</style>
